import React, {useState} from 'react'
import {NavLink} from 'react-router-dom'
import {IconType} from 'react-icons'
import {useReduxAuth} from '../../../../app/modules/auth'
import menuItems from '../../../../app/helpers/menuItems'
import {USER_ROLES} from '../../../../app/constants/StatusConstants'
import {FaChevronDown, FaChevronUp} from 'react-icons/fa'

interface Props {
  collapsed: boolean
}

export interface RouteConfig {
  to: string
  title: string
  allowedRoles: number[]
  children?: RouteConfig[]
}

const NewSidebarMenu: React.FC<Props> = ({collapsed}) => {
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  // Parent menus expanded by default
  const [openParents, setOpenParents] = useState<Set<string>>(() => {
    const parents = new Set<string>();
    menuItems.forEach(item => {
      if (item.children && item.children.length > 0) {
        parents.add(item.title);
      }
    });
    return parents;
  });

  const toggleParent = (title: string) => {
    setOpenParents((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  }

  const linkClass = (isActive: boolean) =>
    `menu-item d-flex align-items-center gap-2 text-decoration-none rounded p-3 transition-base ${
      isActive
        ? 'bg-primary text-white hover-scale'
        : 'text-gray-700 hover-bg-neutral-10 hover-lift'
    } ${collapsed ? 'justify-content-center' : ''}`

  // Now accepts isActive to set icon color
  const renderIcon = (title: string, size = 22, isActive: boolean = false) => {
    // First try to find in top-level menu items
    let def = menuItems.find((mi) => mi.title === title)
    
    // If not found, search in children
    if (!def) {
      for (const item of menuItems) {
        if (item.children) {
          const child = item.children.find((child) => child.title === title)
          if (child) {
            def = child
            break
          }
        }
      }
    }
    
    if (!def) return null
    const IconComp = def.icon as IconType
    return <IconComp fontSize={size} color={isActive ? '#fff' : undefined} />
  }

  // Define role permissions for each menu item
  const getMenuRolePermissions = (title: string) => {
    switch (title) {
      case 'Dashboard':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Doctors':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]
      case 'Sub Admin':
        return [USER_ROLES.ADMIN]
      case 'Medical Assistant':
        return [USER_ROLES.ADMIN]
      case 'Staff':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Patients':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Patient List':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]  
      case 'My Devices':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]
      case 'All Devices':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]
      case 'Time Tracked':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Health Alerts':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Notification Settings':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Payment History':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR]
      case 'Reports':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]  
      case 'Billing Report':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]
      case 'All Billing Reports':
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]
      case 'Cycle Ending Soon':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]
      case 'Subscription':
        return [USER_ROLES.DOCTOR]
      case 'Demo Videos':
        return [USER_ROLES.DOCTOR, USER_ROLES.NURSE,USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, ]  
      default:
        return [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL]
    }
  }

  // Custom menu structure: group Billing Report and Cycle Ending Soon under Reports
  // Use menuItems structure directly for visibleRoutes
  const visibleRoutes: RouteConfig[] = menuItems.map((item) => {
    const children = item.children?.map((child) => ({
      to: child.to,
      title: child.title,
      allowedRoles: getMenuRolePermissions(child.title),
    })).filter((child) => child.allowedRoles.includes(roleId));

    // Special case for Patients menu
    if (item.title === 'Patients') {
      if (roleId === USER_ROLES.ADMIN) {
        // Admin should only see "Patients"
        return {
          to: '/patient',
          title: 'Patients',
          allowedRoles: getMenuRolePermissions('Patient List'),
        };
      }
      // Doctor/Staff should see both "Patients" and "Time Tracked" as submenu
      return {
        to: '#',
        title: 'Patients',
        allowedRoles: getMenuRolePermissions(item.title),
        children: children && children.length > 0 ? children : undefined,
      };
    }

    return {
      to: item.to,
      title: item.title,
      allowedRoles: getMenuRolePermissions(item.title),
      children: children && children.length > 0 ? children : undefined,
    };
  });

  if (roleId == null) {
    return <div className='text-center py-4'>Loading menu…</div>
  }

  // const filteredRoutes = visibleRoutes.filter(
  //   (r) => r.allowedRoles.length === 0 || r.allowedRoles.includes(roleId)
  // )

  // => hiding the subscription route from prod
  const filteredRoutes = visibleRoutes.filter((r) => {
    // Show parent if it has children or if it's a regular route
    const showParent = r.allowedRoles.length === 0 || r.allowedRoles.includes(roleId);
    if (r.children) {
      return r.children.length > 0;
    }
    return showParent;
  });

  return (
    <nav className='d-flex flex-column gap-1 px-2'>
      {filteredRoutes.map((route) => (
        <div key={route.to}>
          {route.children ? (
            // Parent menu item with children (Reports)
            <div>
              <div
                className={
                  `menu-item d-flex align-items-center gap-2 text-decoration-none rounded p-3 transition-base text-gray-700 hover-bg-neutral-10 hover-lift ${collapsed ? 'justify-content-center' : ''} ${openParents.has(route.title) ? 'bg-neutral-10' : ''}`
                }
                style={{cursor: 'pointer'}}
                onClick={(e) => {
                  e.preventDefault();
                  toggleParent(route.title);
                }}
              >
                <span className={`svg-icon-2 ${!collapsed ? 'me-2' : ''}`}>
                  {renderIcon(route.title, 22, false)}
                </span>
                {!collapsed && (
                  <>
                    <span style={{ fontSize: '14px' }}>{route.title}</span>
                    <span className='ms-auto'>
                      {openParents.has(route.title) ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </span>
                  </>
                )}
              </div>
              {!collapsed && openParents.has(route.title) && (
                <div className='ms-4'>
                  {route.children.map((child) => (
                    <NavLink key={child.to} to={child.to}>
                      {({isActive}) => (
                        <div className={linkClass(isActive)}>
                          <span className={`svg-icon-2 ${!collapsed ? 'me-2' : ''}`}>
                            {renderIcon(child.title, 18, isActive)}
                          </span>
                          {!collapsed && <span style={{fontSize: '13px'}}>{child.title}</span>}
                        </div>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Regular menu item without children
            <NavLink key={route.to} to={route.to}>
              {({isActive}) => (
                <div className={linkClass(isActive)}>
                  <span className={`svg-icon-2 ${!collapsed ? 'me-2' : ''}`}>
                    {renderIcon(route.title, 22, isActive)}
                  </span>
                  {!collapsed && <span style={{fontSize: '14px'}}>{route.title}</span>}
                </div>
              )}
            </NavLink>
          )}
        </div>
      ))}
    </nav>
  )
}

export default NewSidebarMenu
